import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dateFormatter } from 'constants/index';
import { useSupplierOfferListQuery } from 'api';
import { ADMIN_ROUTES } from 'constants/routes';
import { TablePaginationConfig, TableProps } from 'components';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { SupplierOfferDataType } from './index';
import { SUPPLIER_OFFER_SOURCE, SUPPLIER_OFFER_TYPE, SupplierOfferType } from 'models/SupplierOffer';

export interface IFilterParams {
  name?: string;
  tags?: string[];
  countryId?: string;
  industryId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  minPurchaseRequests?: number;
  maxPurchaseRequests?: number;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useSupplierOffers = () => {
  const [openFilters, setOpenFilters] = useState(false);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [filters, setFilters] = useState<IFilterParams>({});
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [brandDrawerState, setBrandDrawerState] = useState<boolean>(false);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [brandsData, setBrandsData] = useState<Record<string, any>[] | undefined>();

  const navigate = useNavigate();
  const { data: { content = [], totalElements = 0 } = {}, isLoading } = useSupplierOfferListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);
  const onAddModalStateChange = useCallback((value: boolean) => setAddModalState(value), []);
  const routeChange = useCallback(
    (value: string, type: SupplierOfferType) => {
      const supplierOfferUrl = ADMIN_ROUTES.supplierOffer.supplierOfferUpdate
        .replace(':type', SUPPLIER_OFFER_TYPE[type])
        .replace(':source', SUPPLIER_OFFER_SOURCE.MAIN)
        .replace(':supplierOfferId', value);
      navigate(supplierOfferUrl);
    },
    [navigate]
  );
  const onBrandDrawerExpand = useCallback((clients?: Record<string, any>[]) => {
    setBrandsData(clients);
    setBrandDrawerState((prevState) => !prevState);
  }, []);

  const handleTableChange: TableProps<SupplierOfferDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<SupplierOfferDataType> | SorterResult<SupplierOfferDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.brandId && { brandId: values.brandId }),
        ...(values.supplierId && { supplierId: values.supplierId }),
        ...(values.totalAmountMin && { totalAmountMin: values.totalAmountMin }),
        ...(values.totalAmountMax && { totalAmountMax: values.totalAmountMax }),
        ...(values.type && { type: values.type }),
        ...(values.createdBy && { createdBy: values.createdBy }),
        ...(values.leadTimeMin && { leadTimeMin: values.leadTimeMin }),
        ...(values.leadTimeMax && { leadTimeMax: values.leadTimeMax }),
        ...(values.leadTime && { leadTime: values.leadTime }),
        ...(values.published !== null && values.published !== undefined && { published: values.published }),
        ...(values.createDate?.[0] && {
          createdDateFrom: dateFormatter(values.createDate[0], 'start'),
        }),
        ...(values.createDate?.[1] && {
          createdDateTo: dateFormatter(values.createDate[1], 'end'),
        }),
        ...(values.supplierOfferValidity?.[0] && {
          supplierOfferValidityFrom: dateFormatter(values.supplierOfferValidity[0], 'start'),
        }),
        ...(values.supplierOfferValidity?.[1] && {
          supplierOfferValidityTo: dateFormatter(values.supplierOfferValidity[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );

  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    onAddModalStateChange,
    addModalState,
    routeChange,
    data: content?.length ? content[0].responses : [],
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
    onBrandDrawerExpand,
    brandDrawerState,
    brandsData,
  };
};
